import { useEffect, useState } from "react";
import { Icon } from "@b00k/ui";
import { currencyFor, formatPrice } from "@b00k/domain";
import type { Currency, Lang } from "@b00k/domain";
import { useT } from "../i18n/index.js";

/**
 * Przykłady rotujące w karcie hero.
 *
 * Zostają tutaj, a nie w słowniku interfejsu: to treść demonstracyjna, która
 * ilustruje produkt, a nie etykiety, których szuka się przy tłumaczeniu ekranu.
 * Wrzucenie ich do `Dict` rozdęłoby go o jedną czwartą tekstem, którego nikt
 * nigdy nie kliknie.
 *
 * Identyfikatory i godziny są wspólne — są takie same w każdym języku i
 * powtarzanie ich dziewięć razy tylko zapraszało do rozjazdu.
 */
const SZKIELET = [
  { id: "BK-2041 · WEB", t1: "10:00", t2: "" },
  { id: "BK-2042 · WEB", t1: "", t2: "" },
  { id: "BK-2038", t1: "10:00–10:45", t2: "10:45–12:15" },
  { id: "BK-2044", t1: "12:00–13:00", t2: "13:00–14:00" },
  { id: "BK-2045 · WEB", t1: "9:00–9:30", t2: "9:30–10:00" },
] as const;

/**
 * Kwoty demonstracyjne — PODANE dla każdej waluty, nie przeliczane kursem.
 *
 * Ta sama zasada, co w cenniku: karta ma pokazywać kwotę, która na danym rynku
 * wygląda naturalnie, a nie wynik dzielenia przez kurs dnia.
 */
const KWOTY: Record<Currency, readonly number[]> = {
  PLN: [1180, 1410, 330, 330, 420],
  EUR: [275, 330, 78, 78, 98],
  USD: [300, 360, 85, 85, 108],
  CNY: [2100, 2500, 590, 590, 750],
  JPY: [45000, 54000, 12600, 12600, 16000],
  KRW: [410000, 490000, 115000, 115000, 145000],
};

interface Przyklad {
  tag: string;
  a: string;
  am: string;
  b: string;
  bm: string;
}

const EX: Record<Lang, readonly Przyklad[]> = {
  pl: [
    { tag: "NOWA REZERWACJA · NOCLEGI", a: "Pokój 101 · 2 os.", am: "2 NOCE", b: "Apartament 201", bm: "RÓWNOLEGLE" },
    { tag: "NOWA REZERWACJA · WYNAJEM AUT", a: "SUV 4×4", am: "3 DOBY", b: "Auto miejskie", bm: "RÓWNOLEGLE" },
    { tag: "NOWA REZERWACJA · FRYZJER", a: "Strzyżenie", am: "10:00–10:45", b: "Koloryzacja", bm: "10:45–12:15" },
    { tag: "NOWA REZERWACJA · KOSMETYKA", a: "Manicure hybrydowy", am: "12:00–13:00", b: "Masaż klasyczny", bm: "13:00–14:00" },
    { tag: "NOWA REZERWACJA · MEDYCZNE", a: "Konsultacja lekarska", am: "9:00–9:30", b: "USG jamy brzusznej", bm: "9:30–10:00" },
  ],
  en: [
    { tag: "NEW BOOKING · STAYS", a: "Room 101 · 2 p.", am: "2 NIGHTS", b: "Apartment 201", bm: "PARALLEL" },
    { tag: "NEW BOOKING · CAR RENTAL", a: "SUV 4×4", am: "3 DAYS", b: "City car", bm: "PARALLEL" },
    { tag: "NEW BOOKING · HAIRDRESSER", a: "Haircut", am: "10:00–10:45", b: "Coloring", bm: "10:45–12:15" },
    { tag: "NEW BOOKING · BEAUTY", a: "Gel manicure", am: "12:00–13:00", b: "Classic massage", bm: "13:00–14:00" },
    { tag: "NEW BOOKING · MEDICAL", a: "Doctor consultation", am: "9:00–9:30", b: "Abdominal ultrasound", bm: "9:30–10:00" },
  ],
  de: [
    { tag: "NEUE BUCHUNG · ÜBERNACHTUNG", a: "Zimmer 101 · 2 Pers.", am: "2 NÄCHTE", b: "Apartment 201", bm: "PARALLEL" },
    { tag: "NEUE BUCHUNG · AUTOVERMIETUNG", a: "SUV 4×4", am: "3 TAGE", b: "Stadtauto", bm: "PARALLEL" },
    { tag: "NEUE BUCHUNG · FRISEUR", a: "Haarschnitt", am: "10:00–10:45", b: "Färben", bm: "10:45–12:15" },
    { tag: "NEUE BUCHUNG · KOSMETIK", a: "Gel-Maniküre", am: "12:00–13:00", b: "Klassische Massage", bm: "13:00–14:00" },
    { tag: "NEUE BUCHUNG · MEDIZIN", a: "Ärztliche Beratung", am: "9:00–9:30", b: "Bauch-Ultraschall", bm: "9:30–10:00" },
  ],
  fr: [
    { tag: "NOUVELLE RÉSERVATION · HÉBERGEMENT", a: "Chambre 101 · 2 pers.", am: "2 NUITS", b: "Appartement 201", bm: "EN PARALLÈLE" },
    { tag: "NOUVELLE RÉSERVATION · LOCATION", a: "SUV 4×4", am: "3 JOURS", b: "Citadine", bm: "EN PARALLÈLE" },
    { tag: "NOUVELLE RÉSERVATION · COIFFURE", a: "Coupe", am: "10:00–10:45", b: "Coloration", bm: "10:45–12:15" },
    { tag: "NOUVELLE RÉSERVATION · ESTHÉTIQUE", a: "Manucure gel", am: "12:00–13:00", b: "Massage classique", bm: "13:00–14:00" },
    { tag: "NOUVELLE RÉSERVATION · SANTÉ", a: "Consultation médicale", am: "9:00–9:30", b: "Échographie abdominale", bm: "9:30–10:00" },
  ],
  it: [
    { tag: "NUOVA PRENOTAZIONE · PERNOTTAMENTO", a: "Camera 101 · 2 pers.", am: "2 NOTTI", b: "Appartamento 201", bm: "IN PARALLELO" },
    { tag: "NUOVA PRENOTAZIONE · NOLEGGIO AUTO", a: "SUV 4×4", am: "3 GIORNI", b: "Citycar", bm: "IN PARALLELO" },
    { tag: "NUOVA PRENOTAZIONE · PARRUCCHIERE", a: "Taglio", am: "10:00–10:45", b: "Colore", bm: "10:45–12:15" },
    { tag: "NUOVA PRENOTAZIONE · ESTETICA", a: "Manicure semipermanente", am: "12:00–13:00", b: "Massaggio classico", bm: "13:00–14:00" },
    { tag: "NUOVA PRENOTAZIONE · SANITÀ", a: "Visita medica", am: "9:00–9:30", b: "Ecografia addominale", bm: "9:30–10:00" },
  ],
  es: [
    { tag: "NUEVA RESERVA · ALOJAMIENTO", a: "Habitación 101 · 2 pers.", am: "2 NOCHES", b: "Apartamento 201", bm: "EN PARALELO" },
    { tag: "NUEVA RESERVA · ALQUILER DE COCHES", a: "SUV 4×4", am: "3 DÍAS", b: "Urbano", bm: "EN PARALELO" },
    { tag: "NUEVA RESERVA · PELUQUERÍA", a: "Corte", am: "10:00–10:45", b: "Color", bm: "10:45–12:15" },
    { tag: "NUEVA RESERVA · ESTÉTICA", a: "Manicura semipermanente", am: "12:00–13:00", b: "Masaje clásico", bm: "13:00–14:00" },
    { tag: "NUEVA RESERVA · SALUD", a: "Consulta médica", am: "9:00–9:30", b: "Ecografía abdominal", bm: "9:30–10:00" },
  ],
  zh: [
    { tag: "新预订 · 住宿", a: "101 房 · 2 人", am: "2 晚", b: "201 公寓", bm: "并行" },
    { tag: "新预订 · 租车", a: "SUV 四驱", am: "3 天", b: "城市车", bm: "并行" },
    { tag: "新预订 · 美发", a: "剪发", am: "10:00–10:45", b: "染发", bm: "10:45–12:15" },
    { tag: "新预订 · 美容", a: "光疗美甲", am: "12:00–13:00", b: "经典按摩", bm: "13:00–14:00" },
    { tag: "新预订 · 医疗", a: "医生问诊", am: "9:00–9:30", b: "腹部超声", bm: "9:30–10:00" },
  ],
  ja: [
    { tag: "新規予約 · 宿泊", a: "101 号室 · 2 名", am: "2 泊", b: "アパートメント 201", bm: "並行", },
    { tag: "新規予約 · レンタカー", a: "SUV 4×4", am: "3 日", b: "コンパクトカー", bm: "並行" },
    { tag: "新規予約 · ヘアサロン", a: "カット", am: "10:00–10:45", b: "カラー", bm: "10:45–12:15" },
    { tag: "新規予約 · エステ", a: "ジェルネイル", am: "12:00–13:00", b: "クラシックマッサージ", bm: "13:00–14:00" },
    { tag: "新規予約 · 医療", a: "医師の診察", am: "9:00–9:30", b: "腹部エコー", bm: "9:30–10:00" },
  ],
  ko: [
    { tag: "신규 예약 · 숙박", a: "101호 · 2인", am: "2박", b: "아파트 201", bm: "동시" },
    { tag: "신규 예약 · 렌터카", a: "SUV 4×4", am: "3일", b: "경차", bm: "동시" },
    { tag: "신규 예약 · 미용실", a: "커트", am: "10:00–10:45", b: "염색", bm: "10:45–12:15" },
    { tag: "신규 예약 · 에스테틱", a: "젤 네일", am: "12:00–13:00", b: "클래식 마사지", bm: "13:00–14:00" },
    { tag: "신규 예약 · 의료", a: "의사 진료", am: "9:00–9:30", b: "복부 초음파", bm: "9:30–10:00" },
  ],
};

/** Rotacja co 3000 ms z 450-milisekundowym przenikaniem — jak w prototypie. */
export function HeroCard({ lang }: { lang: Lang }) {
  const t = useT();
  const [idx, setIdx] = useState(0);
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setOpacity(0);
      setTimeout(() => {
        setIdx((i) => (i + 1) % SZKIELET.length);
        setOpacity(1);
      }, 450);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const ex = EX[lang][idx]!;
  const kwota = KWOTY[currencyFor(lang)][idx]!;
  const kanal = SZKIELET[idx]!.id;

  return (
    <div className="ld-card" style={{ opacity }}>
      <div className="ld-card__head">
        <span>{ex.tag}</span>
        <span>{kanal}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="ld-card__row">
          <b>{ex.a}</b>
          <span>{ex.am}</span>
        </div>
        <div className="ld-card__row">
          <b>{ex.b}</b>
          <span>{ex.bm}</span>
        </div>
        <div className="ld-card__foot">
          <span className="ld-card__chip">
            <Icon name="check" size={18} />
            {t.cardChip}
          </span>
          <span className="b00k-mono" style={{ fontSize: 13, fontWeight: 600 }}>
            {formatPrice(kwota, lang)}
          </span>
        </div>
      </div>
      <div className="ld-card__note">{t.cardNote}</div>
    </div>
  );
}
